/**
 * MicrosoftSSOConfig.js
 *
 * A tab inside General Configuration that lets the Admin paste in
 * the Azure AD / MSAL credentials:
 *   - Client ID
 *   - Authority (tenant URL)
 *   - Redirect URI
 *   - Allowed Domain (the company's email domain, e.g. "abc.com")
 *
 * On save, the values are stored:
 *   1. In localStorage (via saveMSALConfig) so the Login page reads them instantly.
 *   2. Posted to the backend via SSO_Slice.saveMSALConfiguration.
 */

import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { employeeID, companyID, sanitizeClientId, sanitizeEmailDomain, normalizeSpaces } from "../../../Common/UIComponents";
import { saveMSALConfig, getMSALConfig } from "../../../Common/msalConfig";
import {
  saveMSALConfiguration,
  fetchMSALConfiguration,
} from "../../../API_Service/Settings_Slice";

const FIELD_META = [
  {
    key: "clientId",
    label: "Client ID",
    placeholder: "e.g. 65ba8938-be7f-4de7-922d-d56c7dab4f1b",
    hint: "Application (client) ID from the Azure App Registration.",
    icon: "fingerprint",
  },
  {
    key: "authority",
    label: "Authority (Tenant URL)",
    placeholder: "https://login.microsoftonline.com/<tenant-id>",
    hint: "Found in Azure AD → Overview. Replace <tenant-id> with your Directory ID.",
    icon: "domain",
  },
  {
    key: "redirectUri",
    label: "Redirect URI",
    placeholder: "http://localhost:3000/ApiClient",
    hint: "Must exactly match a Redirect URI registered in your Azure App.",
    icon: "link",
  },
  {
    key: "allowedDomain",
    label: "Allowed Email Domain",
    placeholder: "abc.com",
    hint: "Only Microsoft accounts from this domain will be allowed in (e.g. abc.com).",
    icon: "alternate_email",
  },
];

// ── tiny shared components ────────────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-bold text-[#3e494a] uppercase tracking-widest ml-1 mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

function TextInput({ value, onChange, placeholder, error, disabled }) {
  return (
    <div>
      <div
        className={`flex items-center bg-[#e5e9e9] rounded-xl px-4 py-3 gap-2
          focus-within:ring-2 focus-within:ring-[#08757c] focus-within:bg-white transition-all
          ${error ? "ring-2 ring-[#ba1a1a]" : ""}
          ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      >
        <input
          type="text"
          value={value}
          onChange={(e) => { const v = e.target.value.replace(/^\s+/, ""); onChange({ ...e, target: { ...e.target, value: v } }); }}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent outline-none text-sm text-[#181c1d] placeholder:text-[#3e494a]/40 font-mono"
        />
      </div>
      {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function MicrosoftSSOConfig({ embedded = true }) {
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    clientId: "",
    authority: "",
    redirectUri: "http://localhost:3000/ApiClient",
    allowedDomain: "",
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    loadMSALConfig();
  }, []);

  const loadMSALConfig = async () => {
    try {
      const response = await dispatch(
        fetchMSALConfiguration({ companyID }),
      ).unwrap();

      console.log("Fetched Config", response);

      if (response) {
        setForm({
          clientId: response.clientId || "",
          authority: response.authority || "",
          redirectUri:
            response.redirectUri || "",
          allowedDomain: response.allowedDomain || "",
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (key) => (e) => {
    let value = e.target.value;

    if (key === "clientId") {
      value = sanitizeClientId(value);
    } else if (key === "allowedDomain") {
      value = sanitizeEmailDomain(value);
    }

    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setSaveSuccess(false);
    setSaveError(null);
  };

  const validate = () => {
    const errs = {};
    if (!form.clientId.trim()) errs.clientId = "Client ID is required.";
    if (!form.authority.trim()) errs.authority = "Authority URL is required.";
    else if (!form.authority.startsWith("https://login.microsoftonline.com/"))
      errs.authority =
        "Authority must start with https://login.microsoftonline.com/";
    if (!form.redirectUri.trim())
      errs.redirectUri = "Redirect URI is required.";
    if (!form.allowedDomain.trim())
      errs.allowedDomain = "Allowed domain is required (e.g. abc.com).";
    else if (form.allowedDomain.includes("@"))
      errs.allowedDomain =
        "Enter only the domain, not the full email (e.g. abc.com).";
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const config = {
      clientId: normalizeSpaces(form.clientId),
      authority: normalizeSpaces(form.authority),
      redirectUri: normalizeSpaces(form.redirectUri),
      allowedDomain: normalizeSpaces(form.allowedDomain).toLowerCase(),
    };

    setIsSaving(true);
    setSaveError(null);

    try {
      console.log("Saving config", config);

      saveMSALConfig(config);

      const response = await dispatch(
        saveMSALConfiguration({
          companyID,
          employeeID,
          msalConfig: config,
        }),
      ).unwrap();

      console.log("Save response", response);

      // Update form with normalized values
      setForm(config);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      console.error(err);
      setSaveError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={
        embedded ? "text-[#181c1d]" : "px-8 py-8 bg-[#f7fafa] min-h-screen"
      }
    >
      <div className="grid grid-cols-12 gap-8">
        {/* LEFT – info panel */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <div className="bg-white p-6 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#3e494a] mb-4">
              Required Fields
            </h4>
            <div className="space-y-2">
              {FIELD_META.map(({ key, label }) => {
                const filled = !!form[key]?.trim();
                return (
                  <div key={key} className="flex items-center justify-between">
                    <p className="text-xs text-[#3e494a]">{label}</p>
                    <span
                      className={`material-symbols-outlined text-[16px] ${
                        filled ? "text-[#436900]" : "text-[#bdc9c9]"
                      }`}
                    >
                      {filled ? "check_circle" : "radio_button_unchecked"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-[#f1f4f4] p-4 rounded-xl flex gap-3 items-start border border-[#bdc9c9]/10">
            <span className="material-symbols-outlined text-[#08757c] text-[18px] mt-0.5">
              info
            </span>
            <p className="text-xs text-[#3e494a] leading-relaxed">
              These values come from your Azure App Registration. After saving,
              employees who click the shared URL can sign in with their company
              Microsoft account — only{" "}
              <strong>@{form.allowedDomain || "yourdomain.com"}</strong>{" "}
              addresses will be allowed.
            </p>
          </div>
        </div>

        {/* RIGHT – form */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="bg-white p-8 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
            <h3 className="text-base font-bold flex items-center gap-2 mb-6">
              <span className="material-symbols-outlined text-[#08757c] text-[20px]">
                security
              </span>
              Microsoft SSO (Azure AD) Settings
            </h3>

            <div className="space-y-5">
              {FIELD_META.map(({ key, label, placeholder, hint, icon }) => (
                <div key={key}>
                  <FieldLabel required>{label}</FieldLabel>
                  <TextInput
                    value={form[key]}
                    onChange={handleChange(key)}
                    placeholder={placeholder}
                    error={errors[key]}
                  />
                  {hint && !errors[key] && (
                    <p className="text-[11px] text-[#3e494a]/60 mt-1 ml-1 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[13px]">
                        {icon}
                      </span>
                      {hint}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Preview JSON */}
            {form.clientId && form.authority && (
              <div className="mt-6 bg-[#f7fafa] rounded-xl p-4 border border-[#cad6d6]">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#3e494a] mb-2">
                  Config Preview
                </p>
                <pre className="text-[11px] text-[#08757c] font-mono overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(
                    {
                      MSALConfig: {
                        clientId: form.clientId,
                        authority: form.authority,
                        redirectUri: form.redirectUri,
                      },
                      allowedDomain: form.allowedDomain,
                    },
                    null,
                    2,
                  )}
                </pre>
              </div>
            )}
          </div>

          {saveSuccess && (
            <div className="bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
              <span className="material-symbols-outlined text-[20px]">
                check_circle
              </span>
              <span className="font-semibold">
                Microsoft SSO configuration saved. Employees can now sign in
                with @{form.allowedDomain} accounts.
              </span>
            </div>
          )}

          {saveError && (
            <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
              <span className="material-symbols-outlined text-[20px]">
                error
              </span>
              {saveError}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-8 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <span className="material-symbols-outlined text-[18px] animate-spin">
                    progress_activity
                  </span>
                  Saving…
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[18px]">
                    save
                  </span>
                  Save SSO Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
